import { useState, type FormEvent } from 'react';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksDialog } from '../../components/liks/LiksDialog/LiksDialog';
import { LiksTextFormField } from '../../components/liks/LiksTextFormField/LiksTextFormField';
import { useI18n } from '../../i18n/useI18n';

type TenantNamedResourceDialogProps = {
  title: string;
  nameLabel: string;
  namePlaceholder: string;
  submitLabel: string;
  cancelLabel: string;
  closeLabel: string;
  initialName?: string;
  isSubmitting?: boolean;
  onSubmit: (name: string) => void;
  onClose: () => void;
};

export function TenantNamedResourceDialog({
  title,
  nameLabel,
  namePlaceholder,
  submitLabel,
  cancelLabel,
  closeLabel,
  initialName = '',
  isSubmitting = false,
  onSubmit,
  onClose,
}: TenantNamedResourceDialogProps) {
  const { translate } = useI18n();
  const [name, setName] = useState(initialName);
  const trimmedName = name.trim();
  const isSubmitDisabled = isSubmitting || trimmedName === '';

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitDisabled) {
      return;
    }
    onSubmit(trimmedName);
  };

  return (
    <LiksDialog
      isOpen
      as="form"
      size="md"
      title={title}
      onClose={onClose}
      onSubmit={handleSubmit}
      closeLabel={closeLabel}
      closeDisabled={isSubmitting}
      isDismissDisabled={isSubmitting}
      footer={
        <>
          <LiksButton
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {cancelLabel}
          </LiksButton>
          <LiksButton type="submit" disabled={isSubmitDisabled}>
            {isSubmitting ? translate('common.loading') : submitLabel}
          </LiksButton>
        </>
      }
    >
      <LiksTextFormField
        label={nameLabel}
        value={name}
        onChange={setName}
        placeholder={namePlaceholder}
        required
        autoFocus
        disabled={isSubmitting}
      />
    </LiksDialog>
  );
}
